// Which ready-made alarms this boat can actually use.
//
// The library ships ~20 recipes for the whole fleet. Any one boat can use a
// fraction of them: half the registry binds nothing in this build at all, and of
// the half that does, a boat only reports some of the signals. Offering all
// twenty as though they were equal put "Bilge high water" and "Smoke/CO/gas"
// beside "House battery low" on a boat with no bilge switch and no gas
// detector — alarms that would sit compile-pending forever, indistinguishable
// on the page from the ones that would arm in seconds.
//
// So each recipe is sorted into one of three states, and only the first is
// offered up front. The other two stay reachable behind a disclosure (and
// through search) rather than being deleted from the page: a bilge switch you
// have not wired yet is a thing you may well be about to wire, and a recipe that
// vanished without explanation reads as a missing feature.
//
// Pure (no React) so the sorting is node-tested; `AlarmNew` is the shell.

import {
  isDeprecatedTemplate,
  pathFamilyMatcher,
  templateNeedsBoatUpdate,
  type AlertTemplate,
} from './alertTemplates';
import type { Signal } from './signalCatalog';

/**
 * Why a recipe is not on offer, or that it is.
 *
 * The two unavailable reasons are told apart deliberately — they have different
 * fixes, and collapsing them would tell an owner to go wire a sensor for an
 * alarm that would not run even once it was wired.
 *
 * - `available`: this build compiles it AND the boat reports a path its arm can
 *   bind. Adding it arms a real rule.
 * - `no-signal`: this build compiles it, but the boat reports nothing in its
 *   path family. Fix: report the signal (wire the sensor, add the gateway).
 * - `not-supported`: this build cannot compile it for ANY boat — no telemetry
 *   family, no producer, or an operator the v1 evaluator lacks. Nothing the
 *   owner can do; it is on us. NO LIVE RECIPE reaches this today: core ADR-216
 *   (TOM-136) withdrew every recipe that cannot compile anywhere, the wire flags
 *   a withdrawn recipe `deprecated`, and {@link groupRecipes} hides it before it
 *   is sorted. The branch stays for the next recipe that ships ahead of its
 *   compiler arm — and such a recipe must not be withdrawn, because the server
 *   refuses to create a withdrawn one (422 `recipe_withdrawn`).
 * - `needs-boat-update`: WE can compile it and the boat reports the signal, but
 *   the boat's own software predates the recipe. The compiler is bundled into
 *   the boat binary, so this alarm would be stored and watch nothing. The fix is
 *   an update, which is neither of the other two answers.
 */
export type RecipeAvailability = 'available' | 'no-signal' | 'not-supported' | 'needs-boat-update';

/**
 * How a single recipe stands against a boat's reported signals.
 *
 * DEFENSIVE about missing family metadata, in the one direction that cannot
 * mislead: a compilable recipe whose `pathFamily` this build cannot read (older
 * cloud, malformed wire block) counts as `available`. Hiding it would state "your
 * boat can't use this" on the strength of a field we failed to parse — a claim
 * we have no evidence for. Offering it merely repeats today's behaviour.
 */
export function recipeAvailability(
  template: AlertTemplate,
  signals: readonly Signal[],
  boatVersion?: string | null
): RecipeAvailability {
  if (template.compileSupport !== 'compiled') return 'not-supported';
  // BEFORE the signal check, on purpose. A boat can report every tank it owns
  // and still be unable to compile `tank_fuel_low`; saying "your boat isn't
  // reporting this" over a tank it is plainly reporting would send the owner
  // hunting a sensor fault that does not exist.
  if (templateNeedsBoatUpdate(template, boatVersion) !== null) return 'needs-boat-update';
  const matches = pathFamilyMatcher(template.pathFamily);
  if (matches === undefined) return 'available';
  return signals.some((s) => typeof s.path === 'string' && matches(s.path))
    ? 'available'
    : 'no-signal';
}

/** One recipe the boat cannot use today, with the reason it cannot. */
export interface UnavailableRecipe {
  readonly template: AlertTemplate;
  readonly reason: Exclude<RecipeAvailability, 'available'>;
}

/** The library split into what to show and what to tuck away. */
export interface RecipeGroups {
  readonly available: AlertTemplate[];
  readonly unavailable: UnavailableRecipe[];
}

/**
 * Sort a template list into the offered group and the tucked-away group.
 *
 * `catalogReady` is the load-bearing argument. A catalog that is still loading —
 * or that failed to load — is NOT evidence that the boat reports nothing, and
 * reading it that way would sweep the entire library behind the disclosure and
 * greet the owner with an empty Add-alarm page. Until the catalog is genuinely
 * ready every recipe stays on offer, exactly as it was before this filter
 * existed; the page is never worse than it used to be, only better once the
 * signals are in hand.
 *
 * Registry order is preserved within each group (the library is ordered
 * deliberately — bilge, power, engine, navigation, tanks, safety).
 *
 * A SUPERSEDED recipe is dropped from BOTH groups — it is not on offer and it is
 * not "something your boat can't use yet". "Tank high / low" was replaced by
 * three better tank alarms, and once those are reachable there is no state of the
 * world in which an owner should add a new combined one: leaving it in the
 * disclosure would keep advertising the one-alarm-for-five-tanks shape this
 * change exists to retire, under a heading that tells them to go wire something.
 * Every alarm already on it keeps working and keeps rendering everywhere else —
 * this filter is the ADD page only.
 *
 * ONCE THOSE ARE REACHABLE is the whole of {@link supersededToHide}. Read the
 * comment there before simplifying this to a one-line filter.
 */
export function groupRecipes(
  templates: readonly AlertTemplate[],
  signals: readonly Signal[],
  catalogReady: boolean,
  boatVersion?: string | null
): RecipeGroups {
  if (!catalogReady) return { available: [...templates], unavailable: [] };
  const hide = supersededToHide(templates, signals, boatVersion);
  const available: AlertTemplate[] = [];
  const unavailable: UnavailableRecipe[] = [];
  for (const template of templates) {
    if (hide.has(template.recipeId)) continue;
    const state = recipeAvailability(template, signals, boatVersion);
    if (state === 'available') available.push(template);
    else unavailable.push({ template, reason: state });
  }
  return { available, unavailable };
}

/**
 * Which superseded recipes to withhold — the ones this boat has a REPLACEMENT
 * for, and only those.
 *
 * WHY THIS IS NOT `filter(t => !deprecated)`. The recipes that replace the
 * superseded ones are themselves version-gated: they need boat software newer
 * than anything in the field until a release cut opens the gate. Hiding the old
 * ones unconditionally would take "House battery low" and "Tank high / low" —
 * two of the alarms most boats actually want — off the Add-alarm page for every
 * boat in the fleet, for the whole window between this change shipping and that
 * release. Nothing would replace them; the page would simply be missing them.
 * Trading a live capability for tidiness ahead of the feature that justifies it
 * is the wrong side of that trade.
 *
 * So a superseded recipe is withheld when EITHER:
 *
 *  - some AVAILABLE, non-superseded recipe binds one of the same paths this boat
 *    reports — a real replacement, reachable today, on this boat's own gear; or
 *  - the boat reports nothing this recipe could bind at all, so it is not
 *    covering anything and never will until a sensor appears (and when one does,
 *    its replacement is what should be offered).
 *
 * Both tests are made against the boat's OWN catalog rather than a hand-kept
 * "X replaces Y" table. The registry says which recipes are superseded and which
 * paths each one walks; that is enough, and a table would be a second source of
 * truth that the next split silently forgets to update.
 *
 * This self-heals: the day the gate opens, the replacements become `available`
 * and the old cards disappear on their own, with no second change to ship.
 */
function supersededToHide(
  templates: readonly AlertTemplate[],
  signals: readonly Signal[],
  boatVersion: string | null | undefined
): ReadonlySet<string> {
  const hide = new Set<string>();
  const superseded = templates.filter(isDeprecatedTemplate);
  if (superseded.length === 0) return hide;

  const paths = signals.map((s) => s.path).filter((p): p is string => typeof p === 'string');
  // The successors: everything NOT superseded that this boat could add today.
  const successors = templates.filter(
    (t) => !isDeprecatedTemplate(t) && recipeAvailability(t, signals, boatVersion) === 'available'
  );
  const successorMatchers = successors
    .map((t) => pathFamilyMatcher(t.pathFamily))
    .filter((m): m is (path: string) => boolean => m !== undefined);

  for (const old of superseded) {
    const matches = pathFamilyMatcher(old.pathFamily);
    // No readable family ⇒ we cannot reason about coverage. Withhold it: an
    // unreadable pattern is not evidence that this recipe is still needed, and
    // the recipe is superseded either way.
    if (matches === undefined) {
      hide.add(old.recipeId);
      continue;
    }
    const mine = paths.filter((p) => matches(p));
    if (mine.length === 0 || mine.some((p) => successorMatchers.some((m) => m(p)))) {
      hide.add(old.recipeId);
    }
  }
  return hide;
}

/**
 * Can an owner open the form for a tucked-away recipe, or is the card dead?
 *
 * `no-signal` and `not-supported` are both SAVEABLE — the alarm is stored and
 * compiles honest-pending, which is a legitimate thing to want for a sensor you
 * are about to wire. (`not-supported` has no live recipe today; see
 * {@link RecipeAvailability}.) `needs-boat-update` is not: the server refuses the create
 * outright, so opening the form could only ever end in an error the owner cannot
 * act on from that screen.
 */
export function isPickable(reason: UnavailableRecipe['reason']): boolean {
  return reason !== 'needs-boat-update';
}

/**
 * The chip text for a tucked-away recipe — what the owner can do about it.
 *
 * The update chip deliberately does NOT quote the version number. It is a
 * developer-facing string (and, until a release cut assigns it, a placeholder
 * sentinel); what an owner needs is that the boat, not their gear, is what has
 * to change. The API's refusal message carries the number for anyone debugging.
 */
export function unavailableLabel(reason: UnavailableRecipe['reason']): string {
  if (reason === 'no-signal') return 'Your boat isn’t reporting this';
  if (reason === 'needs-boat-update') return 'Needs a newer version on your boat';
  return 'We can’t run this yet';
}
